import type { FieldOrigin } from 'features/libra/ontology/ontologyFieldTypes'
import type { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import isEqual from 'lodash/isEqual'

import { zoneMutationOrigin } from './zoneOrigin'

interface ZoneOriginHint {
  scopedFieldOrigin: FieldOrigin
  listMutationOrigin: FieldOrigin
  order: number
}

export interface ZoneOriginTracking {
  zoneOrigins: ReadonlyMap<string, ZoneOriginHint>
  zoneMutationOrder: number
}

export function trackZoneMutation(
  tracking: ZoneOriginTracking,
  previousZones: readonly ZoneParams[],
  nextZones: readonly ZoneParams[]
): ZoneOriginTracking {
  const previousReviewed = previousZones.filter((zone) => !zone.ai_suggested)
  const nextReviewed = nextZones.filter((zone) => !zone.ai_suggested)
  if (isEqual(previousReviewed, nextReviewed)) {
    return {
      zoneOrigins: tracking.zoneOrigins,
      zoneMutationOrder: tracking.zoneMutationOrder,
    }
  }

  const previousById = new Map(previousReviewed.map((zone) => [zone.id, zone]))
  const nextById = new Map(nextReviewed.map((zone) => [zone.id, zone]))
  const previousIndex = new Map(
    previousReviewed.map((zone, index) => [zone.id, index])
  )
  const nextIndex = new Map(nextReviewed.map((zone, index) => [zone.id, index]))
  const zoneOrigins = new Map(tracking.zoneOrigins)
  const order = tracking.zoneMutationOrder + 1
  const affectedIds = new Set([...previousById.keys(), ...nextById.keys()])

  for (const zoneId of affectedIds) {
    if (
      isEqual(previousById.get(zoneId), nextById.get(zoneId)) &&
      previousIndex.get(zoneId) === nextIndex.get(zoneId)
    ) {
      continue
    }
    const zone = nextById.get(zoneId)
    const previousZone = previousById.get(zoneId)
    const contentChanged = !isEqual(previousZone, zone)
    const listMutationOrigin = zone
      ? (zoneMutationOrigin(previousZone ? [previousZone] : [], [zone]) ??
        'human')
      : 'human'
    zoneOrigins.set(zoneId, {
      scopedFieldOrigin: contentChanged
        ? listMutationOrigin
        : (tracking.zoneOrigins.get(zoneId)?.scopedFieldOrigin ?? 'human'),
      listMutationOrigin: contentChanged ? listMutationOrigin : 'human',
      order,
    })
  }

  return { zoneOrigins, zoneMutationOrder: order }
}

function hasPendingZoneChange(
  zoneId: string,
  currentZones: readonly ZoneParams[],
  savedZones: readonly ZoneParams[],
  currentIndex = new Map(currentZones.map((zone, index) => [zone.id, index])),
  savedIndex = new Map(savedZones.map((zone, index) => [zone.id, index]))
): boolean {
  return (
    !isEqual(
      currentZones.find((zone) => zone.id === zoneId),
      savedZones.find((zone) => zone.id === zoneId)
    ) || currentIndex.get(zoneId) !== savedIndex.get(zoneId)
  )
}

export function acknowledgePageZoneOrigins(
  origins: ReadonlyMap<string, ZoneOriginHint>,
  currentZones: readonly ZoneParams[],
  savedZones: readonly ZoneParams[],
  submittedOrder: number
): Map<string, ZoneOriginHint> {
  const reviewed = currentZones.filter((zone) => !zone.ai_suggested)
  const currentIndex = new Map(reviewed.map((zone, index) => [zone.id, index]))
  const savedIndex = new Map(savedZones.map((zone, index) => [zone.id, index]))
  return new Map(
    [...origins].filter(
      ([zoneId, origin]) =>
        origin.order > submittedOrder &&
        hasPendingZoneChange(
          zoneId,
          reviewed,
          savedZones,
          currentIndex,
          savedIndex
        )
    )
  )
}

export function reconcileZoneOriginsAfterRefresh(
  origins: ReadonlyMap<string, ZoneOriginHint>,
  currentZones: readonly ZoneParams[],
  previousSavedZones: readonly ZoneParams[],
  nextSavedZones: readonly ZoneParams[]
): Map<string, ZoneOriginHint> {
  const reviewed = currentZones.filter((zone) => !zone.ai_suggested)
  const currentIndex = new Map(reviewed.map((zone, index) => [zone.id, index]))
  const nextSavedIndex = new Map(
    nextSavedZones.map((zone, index) => [zone.id, index])
  )
  const previousSavedIndex = new Map(
    previousSavedZones.map((zone, index) => [zone.id, index])
  )
  return new Map(
    [...origins].filter(([zoneId]) => {
      const adopted =
        !isEqual(
          previousSavedZones.find((zone) => zone.id === zoneId),
          nextSavedZones.find((zone) => zone.id === zoneId)
        ) || previousSavedIndex.get(zoneId) !== nextSavedIndex.get(zoneId)
      return (
        !adopted ||
        hasPendingZoneChange(
          zoneId,
          reviewed,
          nextSavedZones,
          currentIndex,
          nextSavedIndex
        )
      )
    })
  )
}

export function acknowledgeScopedZoneOrigins(
  origins: ReadonlyMap<string, ZoneOriginHint>,
  currentZones: readonly ZoneParams[],
  savedZones: readonly ZoneParams[],
  baselineZones: readonly ZoneParams[],
  selectedZoneId: string
): Map<string, ZoneOriginHint> {
  const reviewed = currentZones.filter((zone) => !zone.ai_suggested)
  const refreshed = reconcileZoneOriginsAfterRefresh(
    origins,
    currentZones,
    baselineZones,
    savedZones
  )
  return new Map(
    [...refreshed].filter(
      ([zoneId]) =>
        zoneId !== selectedZoneId ||
        hasPendingZoneChange(zoneId, reviewed, savedZones)
    )
  )
}

export function latestZoneListOrigin(
  origins: ReadonlyMap<string, ZoneOriginHint>
): FieldOrigin | undefined {
  let latestOrder = -1
  let latestWasAi = false
  for (const origin of origins.values()) {
    if (origin.order > latestOrder) {
      latestOrder = origin.order
      latestWasAi = origin.listMutationOrigin === 'llm'
    } else if (
      origin.order === latestOrder &&
      origin.listMutationOrigin === 'llm'
    ) {
      latestWasAi = true
    }
  }
  return latestOrder < 0 ? undefined : latestWasAi ? 'llm' : 'human'
}
